import { useState } from 'react';
import { Loader2, Plus } from 'lucide-react';

import { AttachDocumentDialog } from '@/components/documents/AttachDocumentDialog';
import {
  INVOICE_CONTEXT_ONLY_TAG_HINT,
  INVOICE_DOCUMENT_TYPES_HINT,
} from '@/components/fund-costs/fundCostInvoiceHints';
import { Button } from '@/components/ui/button';
import { useEntityFolder } from '@/hooks/react-query/useDocuments';
import { useLinkFundTransactionDocument } from '@/hooks/react-query/useFundTransactions';
import { DocumentSource } from '@/types/document';
import { FUND_TRANSACTION_ACCEPTED_DOC_TAGS } from '@/types/fund-transactions';

interface FundCostInvoiceAttachButtonProps {
  fundId: string;
  /** The cost's payment. */
  transactionId: string;
}

export function FundCostInvoiceAttachButton({
  fundId,
  transactionId,
}: FundCostInvoiceAttachButtonProps) {
  const [open, setOpen] = useState(false);
  const folderQuery = useEntityFolder('FUND', fundId);
  const linkDocument = useLinkFundTransactionDocument(fundId, transactionId);
  const folderId = folderQuery.data?.folder_id;

  return (
    <div className="flex items-center gap-2">
      {folderQuery.isError && (
        <span className="text-xs text-destructive">
          Fund folder unavailable
        </span>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={!folderId}
      >
        {folderQuery.isLoading ? (
          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
        ) : (
          <Plus className="h-4 w-4 mr-1" />
        )}
        Attach
      </Button>
      {folderId && (
        <AttachDocumentDialog
          open={open}
          onOpenChange={setOpen}
          entityType="FUND"
          entityId={fundId}
          searchCollectionId={folderId}
          acceptedTags={
            new Set(FUND_TRANSACTION_ACCEPTED_DOC_TAGS.FUND_COST ?? [])
          }
          documentSource={DocumentSource.UPLOADED}
          documentTypesHint={INVOICE_DOCUMENT_TYPES_HINT}
          contextOnlyTagHint={INVOICE_CONTEXT_ONLY_TAG_HINT}
          onAttach={async (documentId, documentTypes) => {
            await linkDocument.mutateAsync({
              document_id: documentId,
              document_types: documentTypes,
            });
          }}
        />
      )}
    </div>
  );
}
